import {MatchMessage} from './MatchMessage';
import {Model} from './Model';

export type MatchType = 'simple' | 'boost' | 'super_smash';
export type MatchStatus = 'NEW' | 'OPEN' | 'CLOSED' | 'EXPIRED';
export type MatchInteractionType = 'texting' | 'uploading_image';

export type MatchInteraction = {
  match_id: number;
  payload?: string;
  type: MatchInteractionType;
};

export function matchInteractionEqual(
  a: MatchInteraction,
  b: MatchInteraction,
): boolean {
  return (
    a.match_id === b.match_id && a.type === b.type && a.payload === b.payload
  );
}

export function matchInteractionEqualPredicate(
  interaction: MatchInteraction,
): (interaction: MatchInteraction) => boolean {
  return (i: MatchInteraction) => matchInteractionEqual(i, interaction);
}

export function matchSortByLastAt(a: Match, b: Match): number {
  if (a.last_at < b.last_at) {
    return 1;
  } else if (a.last_at > b.last_at) {
    return -1;
  }
  return 0;
}

export function matchSortByFavouriteAt(a: Match, b: Match): number {
  if (a.favourite_at && !b.favourite_at) {
    return -1;
  }
  if (!a.favourite_at && b.favourite_at) {
    return 1;
  }
  if (a.favourite_at && b.favourite_at) {
    if (a.favourite_at < b.favourite_at) {
      return 1;
    } else if (a.favourite_at > b.favourite_at) {
      return -1;
    }
  }
  return matchSortByLastAt(a, b);
}

export enum MatchCallPermission {
  // DO NOT MODIFY THIS ENUM, Only appending is allowed
  none,
  pending,
  declined,
  approved,
  revoked,
  canceled,
}

export type Match = Model & {
  call_permission_init_by_user_id?: number;
  call_permission_status: MatchCallPermission;
  closed_by_user_id?: number;
  compliment?: boolean;
  expires_at?: number;
  extra_messages?: MatchMessage[];
  favourite_at?: number;
  files_urls_query: string;
  has_unread_reaction?: true;
  is_read: boolean;
  last_at: number;
  last_messages?: MatchMessage[];
  last_modified_at?: number;
  last_read_at?: number;
  last_read_me_at?: number;
  notify_online?: boolean;
  pending_delete_at?: number;
  reported?: boolean;
  status: MatchStatus;
  tr: boolean;
  type: MatchType;
  user_id: number;
  user_id_pending_location_verification?: number;
  user_id_pending_verification?: number;
};

export function isMatchNewSimpleEqual(a: Match, b: Match): boolean {
  return (
    a.id === b.id &&
    a.is_read === b.is_read &&
    a.compliment === b.compliment &&
    a.tr === b.tr &&
    a.files_urls_query === b.files_urls_query
  );
}

export function isMatchOpenSimpleEqual(a: Match, b: Match): boolean {
  if (!isMatchNewSimpleEqual(a, b)) {
    return false;
  }
  if (
    !(
      a.has_unread_reaction === b.has_unread_reaction &&
      a.status === b.status &&
      a.last_at === b.last_at &&
      a.call_permission_status === b.call_permission_status &&
      a.last_messages &&
      b.last_messages
    )
  ) {
    return false;
  }
  if (a.last_messages.length > 0 && b.last_messages.length > 0) {
    const la = a.last_messages[0];
    const lb = b.last_messages[0];

    return la.id === lb.id && la.type === lb.type;
  }
  return true;
}

export function canOnlineBeShownForMatch(match: Match): boolean {
  return match.status !== 'CLOSED' && match.status !== 'EXPIRED';
}

export function isFavourite(match: Match): boolean {
  return match.favourite_at !== undefined;
}
